import React, { FC, useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import IconReleaseStoreModeManual from "assets/img/icon-release-store-mode-manual.svg?react"
import IconReleaseStoreModeScan from "assets/img/icon-release-store-mode-scan.svg?react"

import { Grid, GridItem } from "components/Grid"
import { Flex, FlexItem } from "components/Flex"

import Select, { ISelectOption } from "components/Select/Select"
import { FilterCategory, GroupedControl, FilterCategoryToLabelMap, ArticleSearchScope, Purpose, AutocompleteField } from "config/constants"
import { IAppliedFilter, IMultipleEntities, Guid, Lmid, IAssetLmidDto } from "@/types"
import ArticleService from "services/ArticleService"
import AssetService from "services/AssetService"
import SelectSearch from "components/Select/SelectSearch"
import FilterTags from "./FilterTags"
import { ArticleShort } from "models/ArticleShort"
import SelectSearchOptionArticle from "components/Select/SelectSearchOptionArticle"
import InventorySearch from "components/InventorySearch/InventorySearch"
import { tooltipExactSearchFilterAll } from "components/Tooltip/presets/tooltipExactSearch"
import Highlighter from "components/Highlighter/Highlighter"
import StatusIndicator, { StatusIndicatorState } from "components/StatusIndicator/StatusIndicator"
import articleSidebars from "stores/articleSidebars"
import { getAssetOwnershipTypeLabel, getAssetStoredStatusLabel, getDateStringFromMomentDate, getInvoiceAssignmentStatusLightType, getLabeledStatusLabel, getRoleLabel, getValuationStatusLabel } from "utils/utils"
import { ValuationStatus } from "models/Valuation"
import FinanceService from "services/FinanceService"
import { InvoiceAssignmentStatus } from "models/Invoice"
import Reservation, { IGetReservationNumbersItemDto, ReservationStatus } from "models/Reservation"
import TenantEmployee from "models/TenantEmployee"
import { SelectEmployeeOption } from "components/Select/SelectEmployee"
import SelectColor from "components/Select/SelectColor"
import { getShippingOrderDisplayStatusLabel, ShippingOrderDisplayStatus, ShippingOrderType } from "models/ShippingOrder"
import { AssetStoredStatus } from 'models/AssetDisplayStatus'
import { AssetOwnershipType } from "models/Asset"
import useAssetPurposeOptions from "./hooks/useAssetPurposeOptions"
import useValuationStatusOptions from "./hooks/useValuationStatusOptions"
import useReservationStatusOptions from "./hooks/useReservationStatusOptions"
import useReservationPurposeOptions from "./hooks/useReservationPurposeOptions"
import StatusLight from "components/StatusLight/StatusLight"
import useTenantWarehouseOptions from "./hooks/useTenantWarehouseOptions"
import useTenantEmployeeOptions from "./hooks/useTenantEmployeeOptions"
import { getOutgoingInvoiceStatusLabel, getOutgoingInvoiceTypeLabel, OutgoingInvoiceStatus, OutgoingInvoiceType } from "models/OutgoingInvoice"
import { getNotificationChannelLabel, getNotificationTypeLabel, NotificationChannel, NotificationType } from "models/Notification"
import { getOfferStatusLabel, OfferStatus, formatOfferNumber } from "models/Offer"
import ProfitCenter from "models/ProfitCenter"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import useProfitCenters from "hooks/useProfitCenters"
import useProductHierarchy from "hooks/useProductHierarchy"
import useFilter from "./hooks/useFilter"
import useCostCategoryOptions from "./hooks/useCostCategoryOptions"
import CostCategory from "models/CostCategory"
import { ShippingMode } from "models/ShippingOrderOverviewItem"
import useLabelCategories from "hooks/useLabelCategories"
import { getLabelStatusText, LabelStatus } from "models/LabelGroup"
import useAllUsers from "hooks/useAllUsers"
import InputDateTime from "components/InputDateTime/InputDateTime"
import { Role } from "models/UserRoles"
import useMaintenanceTypes from "hooks/useMaintenanceTypes"
import { AssetMaintenanceStatus } from "models/AssetMaintenance"
import { getMaintenanceStatusLabel } from "components/AssetMaintenanceStatusTag/AssetMaintenanceStatusTag"
import { FilterStorageName } from "stores/filters"
import useAssetCategoriesOptions from "./hooks/useAssetCategoriesOptions"

export interface IFilterProps {
    onFiltersChange?(filters: IAppliedFilter[]): void
    filterAppliedFilters?(filters: IAppliedFilter[]): IAppliedFilter[]
    filterCategories: (FilterCategory | { title: string, categories: FilterCategory[] })[]
    includeAssets?: boolean
    includeCanceledRequests?: boolean
    includeRejectedOffers?: boolean
    filters?: IAppliedFilter[]
    storage?: FilterStorageName
    view?: "horizontal" | "vertical"
    filterOptions?: Partial<Record<FilterCategory, ISelectOption[] | null | undefined>>
    onlyUserProfitCenters?: boolean
}

const Filter: FC<React.PropsWithChildren<IFilterProps>> = ({
    onFiltersChange,
    filterAppliedFilters,
    filterCategories: filterCategoriesProp,
    filters: initialFilters,
    includeAssets,
    includeCanceledRequests,
    includeRejectedOffers,
    storage,
    view = "horizontal",
    filterOptions,
    onlyUserProfitCenters,
}) => {
    const { t } = useTranslation()
    const filterCategoriesPrepared = useMemo(() => {
        // customerNameSearch should be hidden from filter categories
        return filterCategoriesProp.filter(fc => fc !== FilterCategory.customerNameSearch)
    }, [filterCategoriesProp])

    const {
        filters,
        filtersLabeled,
        addFilter,
        replaceFilter,
        removeFilter,
    } = useFilter({
        storage,
        initialFilters,
        filterAppliedFilters,
        onlyUserProfitCenters,
    })

    useEffect(() => {
        if (!storage && onFiltersChange) {
            onFiltersChange(filters)
        }
    }, [filters, onFiltersChange, storage])

    const [currentFilterCategory, setCurrentFilterCategory] = useState<FilterCategory>(
        typeof filterCategoriesPrepared[0] === "string"
            ? filterCategoriesPrepared[0]
            : filterCategoriesPrepared[0].categories[0]
    )
    const filterCategories: ISelectOption<FilterCategory>[] =
        filterCategoriesPrepared.map(item => {
            if (typeof item === "string") {
                return {
                    label: t(FilterCategoryToLabelMap[item] as any),
                    value: item
                }
            }
            return item.categories.map(category => ({
                label: t(FilterCategoryToLabelMap[category] as any),
                value: category
            }))
        }).flat()

    const filterCategoriesGrouped: { label: string, options: ISelectOption<FilterCategory>[] }[] =
        filterCategoriesPrepared.map(item => {
            if (typeof item === "string") return null
            return {
                label: item.title,
                options: item.categories.map(category => ({
                    label: t(FilterCategoryToLabelMap[category] as any),
                    value: category
                }))
            }
        }).filter(item => !!item) as { label: string, options: ISelectOption<FilterCategory>[] }[]
    const [forceKeepMenuOpen, setForceKeepMenuOpen] = useState(false)

    const { assetPurposeOptions } = useAssetPurposeOptions(currentFilterCategory)
    const { valuationStatusOptions } = useValuationStatusOptions(currentFilterCategory)
    const { reservationStatusOptions } = useReservationStatusOptions(currentFilterCategory)
    const { reservationPurposeOptions } = useReservationPurposeOptions(currentFilterCategory)
    const { tenantWarehouseOptions, noWarehouseId } = useTenantWarehouseOptions(
        currentFilterCategory === FilterCategory.warehouse
        || currentFilterCategory === FilterCategory.reservationWarehouse
        || currentFilterCategory === FilterCategory.homebase
    )
    const { tenantEmployeeOptions } = useTenantEmployeeOptions(currentFilterCategory)
    const { tenantProfitCenterOptions, userProfitCenterOptions } = useProfitCenters()
    const profitCenterOptions = onlyUserProfitCenters ? userProfitCenterOptions : tenantProfitCenterOptions
    const { usersSelectOptions } = useAllUsers(!filterOptions?.[FilterCategory.user] && currentFilterCategory === FilterCategory.user)
    const { costCategoriesOptions, isLoadingCostCategories } = useCostCategoryOptions(currentFilterCategory)
    const { labelCategoryOptions } = useLabelCategories({ autoFetch: currentFilterCategory === FilterCategory.labelCategory })
    const { maintenanceTypeSelectOptions } = useMaintenanceTypes()
    const { assetCategoriesOptions } = useAssetCategoriesOptions(currentFilterCategory)

    const labelStatusOptions = Object.values(LabelStatus).map(status => ({ label: getLabelStatusText(status), value: status }))

    const {
        fetchProductHierarchy,
        productLineSelectOptions,
        getProductFamilySelectOptions,
        getProductModelSelectOptions,
    } = useProductHierarchy()

    useEffect(() => {
        if (currentFilterCategory === FilterCategory.productLine
            || currentFilterCategory === FilterCategory.productFamily
            || currentFilterCategory === FilterCategory.productModel) {
            fetchProductHierarchy({
                onlyWithArticles: true,
            })
        }
    }, [currentFilterCategory, fetchProductHierarchy])

    const productFamilySelectOptions = useMemo(() => {
        return currentFilterCategory === FilterCategory.productFamily ? getProductFamilySelectOptions() : undefined
    }, [currentFilterCategory, getProductFamilySelectOptions])

    const productModelSelectOptions = useMemo(() => {
        return currentFilterCategory === FilterCategory.productModel ? getProductModelSelectOptions() : undefined
    }, [currentFilterCategory, getProductModelSelectOptions])

    const warehouseOptions = useMemo((): ISelectOption<Guid | null>[] | undefined => {
        if (!tenantWarehouseOptions) return
        return noWarehouseId
            ? [
                ...tenantWarehouseOptions,
                { label: t("common.noWarehouse"), value: null }
            ]
            : tenantWarehouseOptions
    }, [noWarehouseId, t, tenantWarehouseOptions])

    const homebaseAndReservationWarehouseOptions = useMemo((): ISelectOption<Guid | null>[] | undefined => {
        if (!tenantWarehouseOptions) return
        return [
            ...tenantWarehouseOptions,
            ...noWarehouseId
                ? [{ label: t("common.noWarehouse"), value: noWarehouseId }]
                : [],
            { label: t("common.noValue"), value: null }
        ]
    }, [noWarehouseId, t, tenantWarehouseOptions])

    const responsibleWarehouseOptions = useMemo((): ISelectOption<Guid | null>[] | undefined => {
        if (!tenantWarehouseOptions) return
        return [
            ...tenantWarehouseOptions,
            { label: t("common.noValue"), value: null }
        ]
    }, [t, tenantWarehouseOptions])



    const handleAllFilterChange = (item: ArticleShort) => {
        if (item instanceof ArticleShort) {
            onArticleSelect(item)
        }
    }

    function getSearchResults(criteria?: string): Promise<(IMultipleEntities<any> | null)[]> {
        return Promise.all([
            ArticleService.getArticles({ criteria, isOnlyForHolder: true })
        ])
    }

    async function onArticleSelect(articleShort: ArticleShort) {
        addFilter({
            category: FilterCategory.article,
            value: articleShort.id,
            label: articleShort.articleNo
        })
    }

    async function onShowArticleDetailsClick(articleShort: ArticleShort) {
        setForceKeepMenuOpen(true)
        articleSidebars.show({
            articleId: articleShort.id,
            onClose: () => {
                setForceKeepMenuOpen(false)
            }
        })
    }

    function renderAssignmentStatusOption(option: { label: string, value: InvoiceAssignmentStatus }) {
        return <Flex alignItems="center">
            <div className="mr-1">
                <StatusLight type={getInvoiceAssignmentStatusLightType(option.value)} />
            </div>
            {option.label}
        </Flex>
    }

    function renderValuationStatusOption({ value }: { label: ValuationStatus, value: ValuationStatus }) {
        return <Flex alignItems="center">
            <div className="mr-1">
                <StatusIndicator state={value === ValuationStatus.active ? StatusIndicatorState.green : (value === ValuationStatus.disposal ? StatusIndicatorState.grey : StatusIndicatorState.red)} />
            </div>
            {getValuationStatusLabel(value)}
        </Flex>
    }

    function renderSelectSearchOption(entity: ArticleShort, search: string) {
        if (entity instanceof ArticleShort) {
            return renderSelectSearchOptionArticle(entity, search)
        }
        return <></>
    }

    function renderSelectSearchOptionArticle(articleShort: ArticleShort, highlight: string) {
        return <SelectSearchOptionArticle
            article={articleShort}
            onShowDetailsClick={() => onShowArticleDetailsClick(articleShort)}
            highlight={highlight}
        />
    }

    function renderSelectSearchOptionCostCategory(option: ISelectOption<CostCategory>) {
        return <Flex justifyContent='space-between'>
            <FlexItem className='ellipsis max-w-40'>
                {option.value.costCategoryLedgerAccount || t("common.noValue")}
            </FlexItem>
            <FlexItem className="ellipsis color-grey-500 pl-1 max-w-60">
                {option.value.costCategoryName}
            </FlexItem>
        </Flex>
    }

    if (storage && initialFilters) {
        throw new Error("Filter component: pass only 'storage' or 'filters', not both")
    }

    const tags = (
        <FilterTags
            filters={filtersLabeled}
            onTagRemove={removeFilter}
            justify={view === "vertical" ? "start" : "end"}
        />
    )

    return (
        <>
            <Grid
                columns={view === "horizontal" ? "minmax(0, auto) minmax(0, 560px)" : "1fr"}
                gap="2.4rem"
                alignItems="center"
                className="w-100">
                {view === "horizontal" && tags}
                <Grid
                    columns={view === "horizontal" ? "minmax(0, 3fr) minmax(0, 4fr)" : "max-content minmax(0, auto)"}>
                    <GridItem>
                        <Select
                            options={filterCategories}
                            groupOptions={filterCategoriesGrouped.length ? () => filterCategoriesGrouped : undefined}
                            value={currentFilterCategory}
                            onChange={item => setCurrentFilterCategory(item?.value || FilterCategory.all)}
                            grouped={GroupedControl.left}
                            testIdPrefix="filter-type-select"
                        />
                    </GridItem>
                    <GridItem>
                        {currentFilterCategory === FilterCategory.all &&
                            <InventorySearch
                                onSelect={addFilter}
                                includeAssets={includeAssets}
                                isOnlyForHolder
                                allowCustomQuery
                                grouped={GroupedControl.right}
                                menuWidth="560px"
                                menuAlign="right"
                                placeholder={t('common.searchAndFilter')}
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.article &&
                            <SelectSearch
                                getOptions={search => getSearchResults(search)}
                                placeholder={t('common.searchAndFilter')}
                                onChange={handleAllFilterChange}
                                optionTemplate={renderSelectSearchOption}
                                grouped={GroupedControl.right}
                                menuWidth="450px"
                                menuAlign="right"
                                forceKeepMenuOpen={forceKeepMenuOpen}
                                helpTooltip={tooltipExactSearchFilterAll}
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.articleType &&
                            <Select
                                placeholder={t('common.searchAndFilter')}
                                options={[
                                    { label: t("common.machines"), value: ArticleSearchScope.machine },
                                    { label: t("common.equipment"), value: ArticleSearchScope.equipment }
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.articleType, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.assetPurpose &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.assetPurpose, value: item?.value })}
                                options={assetPurposeOptions}
                                placeholder={t(assetPurposeOptions ? 'common.searchAndFilter' : "common.loading")}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.productLine &&
                            <Select
                                options={productLineSelectOptions}
                                placeholder={t('common.searchAndFilter')}
                                onChange={item => addFilter({ category: FilterCategory.productLine, value: item?.value, label: item?.label })}
                                filterOption={(option, search) => {
                                    return option.label.toLowerCase().includes(search.toLowerCase())
                                }}
                                grouped={GroupedControl.right}
                                testIdPrefix="filter-select"
                                clearOnChange
                            />
                        }
                        {currentFilterCategory === FilterCategory.productFamily &&
                            <Select
                                options={productFamilySelectOptions}
                                placeholder={t('common.searchAndFilter')}
                                onChange={item => addFilter({ category: FilterCategory.productFamily, value: item?.value, label: item?.label })}
                                filterOption={(option, search) => {
                                    return option.label.toLowerCase().includes(search.toLowerCase())
                                }}
                                grouped={GroupedControl.right}
                                testIdPrefix="filter-select"
                                clearOnChange
                            />
                        }
                        {currentFilterCategory === FilterCategory.productModel &&
                            <Select
                                options={productModelSelectOptions}
                                placeholder={t('common.searchAndFilter')}
                                onChange={item => addFilter({ category: FilterCategory.productModel, value: item?.value, label: item?.label })}
                                filterOption={(option, search) => {
                                    return option.label.toLowerCase().includes(search.toLowerCase())
                                }}
                                grouped={GroupedControl.right}
                                testIdPrefix="filter-select"
                                clearOnChange
                            />
                        }
                        {currentFilterCategory === FilterCategory.yearOfConstruction &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.yearOfConstruction, value: item ? +item.value : undefined })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.constructionYear, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.emissionLevel &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.emissionLevel, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.emissionLevel, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.inventoryNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.inventoryNumber, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.inventoryNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.serialNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.serialNumber, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.serialNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.serialNumberMachine &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.serialNumberMachine, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.serialNumberMachine, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.manufacturerCode &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.manufacturerCode, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.manufacturerCode, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.lmid &&
                            <Select<IAssetLmidDto>
                                onChange={item => addFilter({ category: FilterCategory.lmid, value: item?.value.id, label: item?.value.lmid })}
                                getOptions={(search) => AssetService.getLmids({ search })}
                                formatOptionLabel={(option: { id: Guid, lmid: Lmid }) => option.lmid}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.financeAssetLmid &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.financeAssetLmid, value: item?.value })}
                                getOptions={(search) => FinanceService.getLmids({ search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.financeSupplierOrderNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.financeSupplierOrderNumber, value: item?.value })}
                                getOptions={(search) => FinanceService.getSupplierOrderNumbers({ search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.financeInternalOrderNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.financeInternalOrderNumber, value: item?.value })}
                                getOptions={(search) => FinanceService.getFinanceInternalOrderNumbers({ search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.valuationStatus &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.valuationStatus, value: item?.value })}
                                options={valuationStatusOptions}
                                optionTemplate={renderValuationStatusOption}
                                placeholder={t(valuationStatusOptions ? 'common.searchAndFilter' : "common.loading")}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.invoiceNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.invoiceNumber, value: item?.value })}
                                getOptions={(search) => FinanceService.getInvoiceNumbers({ search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.referenceNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.referenceNumber, value: item?.value })}
                                getOptions={(search) => FinanceService.getInvoiceReferenceNumbers({ search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.referenceText &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.referenceText, value: item?.value })}
                                getOptions={(search) => FinanceService.getInvoiceReferenceTexts({ search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.assignmentStatus &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.assignmentStatus, value: item?.value })}
                                options={[
                                    { label: t("invoices.assigned"), value: InvoiceAssignmentStatus.assigned },
                                    { label: t("invoices.assignedAutomatically"), value: InvoiceAssignmentStatus.assignedAutomatically },
                                    { label: t("invoices.notAssigned"), value: InvoiceAssignmentStatus.notAssigned },
                                    { label: t("invoices.notRelevant"), value: InvoiceAssignmentStatus.notRelevant }
                                ]}
                                placeholder={t('common.searchAndFilter')}
                                optionTemplate={renderAssignmentStatusOption}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.customerName &&
                            <SelectSearch
                                getOptions={(search) => Promise.all([AssetService.getAutocomplete({ field: AutocompleteField.customerName, search })])}
                                placeholder={t('common.searchAndFilter')}
                                onChange={(value: string) => addFilter({ category: FilterCategory.customerName, value })}
                                onAdd={value => addFilter({ category: FilterCategory.customerNameSearch, value })}
                                optionTemplate={(value: string, highlight: string) => (
                                    <Highlighter
                                        text={value}
                                        highlight={highlight}
                                    />
                                )}
                                isCreatable
                                grouped={GroupedControl.right}
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.customerNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.customerNumber, value: item?.value })}
                                getOptions={(search) => AssetService.getAutocomplete({ field: AutocompleteField.customerNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.crmReferenceNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.crmReferenceNumber, value: item?.value })}
                                getOptions={(search) => AssetService.getAutocomplete({ field: AutocompleteField.crmReferenceNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                isCreatable
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.vatCode &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.vatCode, value: item?.value })}
                                getOptions={(search) => AssetService.getAutocomplete({ field: AutocompleteField.vatCode, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.drawingNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.drawingNumber, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.drawingNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.label &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.label, value: item?.value })}
                                options={[
                                    { label: getLabeledStatusLabel(true), value: true },
                                    { label: getLabeledStatusLabel(false), value: false },
                                ]}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.operationalState &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.operationalState, value: item?.value })}
                                options={[
                                    { label: t("asset.assetOperational"), value: true },
                                    { label: t("asset.assetOutOfOrder"), value: false },
                                ]}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.connectedAssets &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.connectedAssets, value: item?.value })}
                                options={[
                                    { label: t("common.yes"), value: true },
                                    { label: t("common.no"), value: false },
                                ]}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.reservationNumber &&
                            <Select<IGetReservationNumbersItemDto>
                                onChange={item => addFilter({ category: FilterCategory.reservationNumber, value: item?.value.number, label: item?.value.number })}
                                getOptions={search => AssetService.getReservationNumbers({ search, includeCanceled: includeCanceledRequests })}
                                optionTemplate={(option: ISelectOption<IGetReservationNumbersItemDto>, highlight: string) => (
                                    <Highlighter
                                        text={option.value.number}
                                        highlight={highlight ? [Reservation.formatReservationNumber(highlight) || "", highlight] : ""}
                                    />
                                )}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.reservationNumberAssetIds &&
                            <Select<IGetReservationNumbersItemDto>
                                onChange={item => addFilter({ category: FilterCategory.reservationNumberAssetIds, value: item?.value.assets, label: item?.value.number })}
                                getOptions={search => AssetService.getReservationNumbers({ search, includeCanceled: includeCanceledRequests })}
                                optionTemplate={(option: ISelectOption<IGetReservationNumbersItemDto>, highlight: string) => (
                                    <Highlighter
                                        text={option.value.number || ""}
                                        highlight={highlight ? [Reservation.formatReservationNumber(highlight) || "", highlight] : ""}
                                    />
                                )}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.reservationStatus &&
                            <Select<ReservationStatus>
                                onChange={item => addFilter({ category: FilterCategory.reservationStatus, value: item?.value })}
                                options={reservationStatusOptions}
                                placeholder={t(reservationStatusOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.salesAdmin &&
                            <Select<TenantEmployee>
                                onChange={item => addFilter({ category: FilterCategory.salesAdmin, value: item?.value.number })}
                                options={tenantEmployeeOptions}
                                optionTemplate={(option: ISelectOption<TenantEmployee>, search?: string) => {
                                    return <SelectEmployeeOption employee={option.value} highlight={search} />
                                }}
                                placeholder={t(tenantEmployeeOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.salesPerson &&
                            <Select<TenantEmployee>
                                onChange={item => addFilter({ category: FilterCategory.salesPerson, value: item?.value.number })}
                                options={tenantEmployeeOptions}
                                optionTemplate={(option: ISelectOption<TenantEmployee>, search?: string) => {
                                    return <SelectEmployeeOption employee={option.value} highlight={search} />
                                }}
                                placeholder={t(tenantEmployeeOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.reservationPurpose &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.reservationPurpose, value: item?.value })}
                                options={reservationPurposeOptions}
                                placeholder={t(reservationPurposeOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.homebase &&
                            <Select
                                onChange={(item: ISelectOption<Guid>) => addFilter({ category: FilterCategory.homebase, value: item.value })}
                                options={homebaseAndReservationWarehouseOptions}
                                placeholder={t(homebaseAndReservationWarehouseOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.reservationWarehouse &&
                            <Select
                                onChange={(item: ISelectOption<Guid>) => addFilter({ category: FilterCategory.reservationWarehouse, value: item.value })}
                                options={homebaseAndReservationWarehouseOptions}
                                placeholder={t(homebaseAndReservationWarehouseOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.warehouse &&
                            <Select
                                onChange={(item: ISelectOption<Guid>) => addFilter({ category: FilterCategory.warehouse, value: item.value })}
                                options={warehouseOptions}
                                placeholder={t(warehouseOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                                isDisabled={!warehouseOptions}
                            />
                        }
                        {currentFilterCategory === FilterCategory.fromToLocation &&
                            <Select
                                onChange={(item: ISelectOption<Guid>) => addFilter({ category: FilterCategory.fromToLocation, value: item.value })}
                                options={tenantWarehouseOptions}
                                placeholder={t(warehouseOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                                isDisabled={!tenantWarehouseOptions}
                            />
                        }
                        {currentFilterCategory === FilterCategory.responsibleWarehouse &&
                            <Select
                                onChange={(item: ISelectOption<Guid>) => addFilter({ category: FilterCategory.responsibleWarehouse, value: item.value })}
                                options={responsibleWarehouseOptions}
                                placeholder={t(responsibleWarehouseOptions ? 'common.searchAndFilter' : 'common.loading')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                                isDisabled={!responsibleWarehouseOptions}
                            />
                        }
                        {currentFilterCategory === FilterCategory.profitCenter &&
                            <Select
                                onChange={(item: ISelectOption<ProfitCenter>) => addFilter({ category: FilterCategory.profitCenter, value: item.value.id })}
                                options={filterOptions?.[FilterCategory.profitCenter] ?? profitCenterOptions}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                optionTemplate={(option: ISelectOption<ProfitCenter>, search?: string) => (
                                    <Flex justifyContent='space-between' className="max-w-25rem" gap="2rem">
                                        <FlexibleContent text={option.value.description} highlight={search} />
                                        <FlexItem shrink={0} className='max-w-50'>
                                            <FlexibleContent text={option.value.code} className="color-grey-500" highlight={search} />
                                        </FlexItem>
                                    </Flex>
                                )}
                                filterOption={(option, search) => {
                                    return `${option.data.value.code!}${option.data.value.description!}`.toLowerCase().includes(search.toLowerCase())
                                }}
                                clearOnChange
                                testIdPrefix="filter-select"
                                isDisabled={!filterOptions?.[FilterCategory.profitCenter] && !profitCenterOptions}
                            />
                        }
                        {currentFilterCategory === FilterCategory.color &&
                            <SelectColor
                                onChange={colorId => addFilter({ category: FilterCategory.color, value: colorId })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.shippingOrderType &&
                            <Select
                                placeholder={t('common.searchAndFilter')}
                                options={[
                                    { label: t("shippingOrderType.delivery"), value: ShippingOrderType.delivery },
                                    { label: t("shippingOrderType.return"), value: ShippingOrderType.return },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.shippingOrderType, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.shippingOrderState &&
                            <Select
                                placeholder={t('common.searchAndFilter')}
                                options={[
                                    { label: getShippingOrderDisplayStatusLabel(ShippingOrderDisplayStatus.loadingListNotCreated), value: ShippingOrderDisplayStatus.loadingListNotCreated },
                                    { label: getShippingOrderDisplayStatusLabel(ShippingOrderDisplayStatus.loadingListInProgress), value: ShippingOrderDisplayStatus.loadingListInProgress },
                                    { label: getShippingOrderDisplayStatusLabel(ShippingOrderDisplayStatus.pushedToStock), value: ShippingOrderDisplayStatus.pushedToStock },
                                    { label: getShippingOrderDisplayStatusLabel(ShippingOrderDisplayStatus.completed), value: ShippingOrderDisplayStatus.completed },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.shippingOrderState, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.shippingOrderNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.shippingOrderNumber, value: item?.value })}
                                getOptions={(search) => AssetService.getAutocomplete({ field: AutocompleteField.shippingOrderNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.stateOfStorage &&
                            <Select
                                placeholder={t('common.searchAndFilter')}
                                options={[
                                    { label: getAssetStoredStatusLabel(AssetStoredStatus.released), value: AssetStoredStatus.released },
                                    { label: getAssetStoredStatusLabel(AssetStoredStatus.stored), value: AssetStoredStatus.stored }
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.stateOfStorage, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.ownershipType &&
                            <Select
                                placeholder={t('common.searchAndFilter')}
                                options={[
                                    { label: getAssetOwnershipTypeLabel(AssetOwnershipType.purchased), value: AssetOwnershipType.purchased },
                                    { label: getAssetOwnershipTypeLabel(AssetOwnershipType.borrowed), value: AssetOwnershipType.borrowed }
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.ownershipType, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.rentToBuy &&
                            <Select
                                placeholder={t('common.searchAndFilter')}
                                options={[
                                    { label: t('common.yes'), value: 1 },
                                    { label: t('common.no'), value: 0 },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.rentToBuy, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.erpInvoiceNumber &&
                            <Select<IAssetLmidDto>
                                onChange={item => addFilter({ category: FilterCategory.erpInvoiceNumber, value: item?.value })}
                                getOptions={(search) => AssetService.getAutocomplete({ field: AutocompleteField.erpInvoiceNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.erpOrderNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.erpOrderNumber, value: item?.value })}
                                getOptions={(search) => AssetService.getAutocomplete({ field: AutocompleteField.erpOrderNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.outgoingInvoiceType &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.outgoingInvoiceType, value: item?.value })}
                                options={Object.values(OutgoingInvoiceType).map(type => ({ label: getOutgoingInvoiceTypeLabel(type), value: type }))}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.outgoingInvoiceStatus &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.outgoingInvoiceStatus, value: item?.value })}
                                options={Object.values(OutgoingInvoiceStatus).map(status => ({ label: getOutgoingInvoiceStatusLabel(status), value: status }))}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.notificationChannel &&
                            <Select
                                placeholder={t('common.searchAndFilter')}
                                options={[
                                    { label: getNotificationChannelLabel(NotificationChannel.sales), value: NotificationChannel.sales },
                                    { label: getNotificationChannelLabel(NotificationChannel.logistics), value: NotificationChannel.logistics },
                                    { label: getNotificationChannelLabel(NotificationChannel.finance), value: NotificationChannel.finance },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.notificationChannel, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.notificationType &&
                            <Select
                                placeholder={t('common.searchAndFilter')}
                                options={Object.values(NotificationType).map(type => ({
                                    label: getNotificationTypeLabel(type, { forFilter: true }),
                                    value: type
                                }))}
                                onChange={item => addFilter({ category: FilterCategory.notificationType, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelIdLinked &&
                            <Select<string>
                                onChange={item => addFilter({ category: FilterCategory.labelIdLinked, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.linkedLabelId, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.offerPurpose &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.offerPurpose, value: item?.value })}
                                options={[
                                    { label: t("purposes.rent"), value: Purpose.rent },
                                    { label: t("purposes.sale"), value: Purpose.sale },
                                ]}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.offerStatus &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.offerStatus, value: item?.value })}
                                options={Object.values(OfferStatus).map(type => ({ label: getOfferStatusLabel(type), value: type }))}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.offerNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.offerNumber, value: item?.value })}
                                getOptions={search => AssetService.getOfferNumbers({ search, includeRejected: includeRejectedOffers })}
                                optionTemplate={(option: ISelectOption<string>, highlight: string) => (
                                    <Highlighter
                                        text={option.value}
                                        highlight={highlight ? [formatOfferNumber(highlight) || "", highlight] : ""}
                                    />
                                )}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.shortage &&
                            <Select
                                options={[
                                    { label: t("common.yes"), value: true },
                                    { label: t("common.no"), value: false },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.shortage, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.startsWithinDays &&
                            <Select
                                options={[
                                    { label: t("common.xDays", { count: 7 }), value: 7 },
                                    { label: t("common.xDays", { count: 30 }), value: 30 },
                                    { label: t("common.xDays", { count: 90 }), value: 90 },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.startsWithinDays, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.endsWithinDays &&
                            <Select
                                options={[
                                    { label: t("common.xDays", { count: 7 }), value: 7 },
                                    { label: t("common.xDays", { count: 30 }), value: 30 },
                                    { label: t("common.xDays", { count: 90 }), value: 90 },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.endsWithinDays, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.automaticInvoicing &&
                            <Select
                                options={[
                                    { label: t("filter.automaticInvoicing.automatic"), value: true },
                                    { label: t("filter.automaticInvoicing.manual"), value: false },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.automaticInvoicing, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.costCategory &&
                            <Select
                                options={costCategoriesOptions}
                                onChange={item => addFilter({ category: FilterCategory.costCategory, label: item?.value.costCategoryName, value: item?.value.costCategoryId })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                placeholder={t(isLoadingCostCategories ? 'common.loading' : 'common.searchAndFilter')}
                                testIdPrefix="filter-select"
                                optionTemplate={renderSelectSearchOptionCostCategory}
                            />
                        }
                        {currentFilterCategory === FilterCategory.shippingOrderCompleteMode &&
                            <Select
                                options={[
                                    { label: t("shippingOrderCompleteMode.manualViaWeb"), value: ShippingMode.web },
                                    { label: t("shippingOrderCompleteMode.scanViaLogisticApp"), value: ShippingMode.mobile },
                                ]}
                                onChange={item => addFilter({ category: FilterCategory.shippingOrderCompleteMode, value: item?.value })}
                                optionTemplate={(option, search) => (
                                    <Flex alignItems="center" gap="0.8rem">
                                        <FlexItem shrink={0}>
                                            {option.value === ShippingMode.web && <IconReleaseStoreModeManual className="d-block" />}
                                            {option.value === ShippingMode.mobile && <IconReleaseStoreModeScan className="d-block" />}
                                        </FlexItem>
                                        <FlexibleContent text={option.label} highlight={search} />
                                    </Flex>
                                )}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelArticleNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelArticleNumber, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.labelArticleNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelSerialNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelSerialNumber, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.labelSerialNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelSerialNumberMachine &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelSerialNumberMachine, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.labelSerialNumberMachine, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelInventoryNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelInventoryNumber, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.labelInventoryNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelManufacturerCode &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelManufacturerCode, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.labelManufacturerCode, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelConstructionYear &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelConstructionYear, value: item?.value ? +item.value : null })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.labelConstructionYear, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelDrawingNumber &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelDrawingNumber, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.labelDrawingNumber, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelIdNotLinked &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelIdNotLinked, value: item?.value })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.notLinkedLabelId, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelCategory &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.labelCategory, value: item?.value })}
                                options={labelCategoryOptions}
                                placeholder={t(labelCategoryOptions ? 'common.searchAndFilter' : "common.loading")}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.labelStatus &&
                            <Select
                                options={labelStatusOptions}
                                onChange={item => addFilter({ category: FilterCategory.labelStatus, value: item?.value })}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.user &&
                            <Select
                                options={filterOptions?.[FilterCategory.user] ?? usersSelectOptions}
                                onChange={item => addFilter({ category: FilterCategory.user, value: item?.value })}
                                optionTemplate={(option: ISelectOption<Guid>, highlight: string) => (
                                    <FlexibleContent
                                        text={option.label}
                                        highlight={highlight}
                                    />
                                )}
                                grouped={GroupedControl.right}
                                clearOnChange
                                isUseOptimization
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.invoiceDate &&
                            <InputDateTime
                                minDetail="year"
                                maxDetail="year"
                                onDateChange={date =>
                                    replaceFilter(FilterCategory.invoiceDate,
                                        [{
                                            category: FilterCategory.invoiceDate,
                                            value: date ? getDateStringFromMomentDate(date) : undefined,
                                            label: date?.format(t("config.dateMonthYearFormat"))
                                        }]
                                    )
                                }
                                grouped={GroupedControl.right}
                                noSaveButton
                            />
                        }
                        {currentFilterCategory === FilterCategory.role &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.role, value: item?.value })}
                                options={Object.values(Role).map(role => ({ label: getRoleLabel(role), value: role }))
                                }
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.maintenanceType &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.maintenanceType, value: item?.value })}
                                options={maintenanceTypeSelectOptions}
                                placeholder={t(maintenanceTypeSelectOptions ? 'common.searchAndFilter' : "common.loading")}
                                filterOption={(option, search) => option.label.toLowerCase().includes(search.toLowerCase())}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.maintenanceStatus &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.maintenanceStatus, value: item?.value })}
                                options={Object.values(AssetMaintenanceStatus).map(type => ({ label: getMaintenanceStatusLabel(type), value: type }))}
                                placeholder={t(maintenanceTypeSelectOptions ? 'common.searchAndFilter' : "common.loading")}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.assetCategory &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.assetCategory, value: item?.value, label: item?.label })}
                                options={assetCategoriesOptions}
                                placeholder={t(assetCategoriesOptions ? 'common.searchAndFilter' : "common.loading")}
                                filterOption={(option, search) => option.label.toLowerCase().includes(search.toLowerCase())}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                        {currentFilterCategory === FilterCategory.referenceNumberSale &&
                            <Select
                                onChange={item => addFilter({ category: FilterCategory.referenceNumberSale, value: item?.value, label: item?.label })}
                                getOptions={search => AssetService.getAutocomplete({ field: AutocompleteField.referenceNumberSale, search })}
                                placeholder={t('common.searchAndFilter')}
                                grouped={GroupedControl.right}
                                clearOnChange
                                testIdPrefix="filter-select"
                            />
                        }
                    </GridItem>
                </Grid>
                {view === "vertical" && tags}
            </Grid>
        </>
    )
}

export default Filter
